import React, { useState, useEffect } from 'react';
import mapboxgl from 'mapbox-gl';

const AddressLookup = ({ 
  map, 
  currentYear, 
  yearData, 
  onAddressFound,
  onAddressCleared,
  sidebar = false,
  selectedSchoolNames = new Set(),
  isFiltering = false,
  mobile = false
}) => {
  const [searchAddress, setSearchAddress] = useState('');
  const [addressMarker, setAddressMarker] = useState(null);
  const [addressAssignments, setAddressAssignments] = useState(null);
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [showResults, setShowResults] = useState(false);

  // MOBILE: Detect if we're on mobile - enhanced detection
  const isMobile = mobile || 
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || 
    window.innerWidth <= 768 ||
    ('ontouchstart' in window) ||
    (navigator.maxTouchPoints > 0);

  // Point-in-polygon algorithm (ray casting)
  const isPointInPolygon = (point, polygon) => {
    const [x, y] = point;
    let inside = false;
    
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const [xi, yi] = polygon[i];
      const [xj, yj] = polygon[j];
      
      if (((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi)) {
        inside = !inside;
      }
    }
    
    return inside;
  };

  // ENHANCED: School name normalization (matching MapContainer logic)
  const normalizeSchoolName = (name, type) => {
    if (!name || typeof name !== 'string') return '';
    
    let normalized = name.trim()
      .replace(/\s+/g, ' ')
      .replace(/\s*(Elementary|ES|E\.S\.)(\s*School)?$/i, '')
      .replace(/\s*(Middle|MS|M\.S\.)(\s*School)?$/i, '')
      .replace(/\s*(High|HS|H\.S\.)(\s*School)?$/i, '')
      .replace(/\s*School$/i, '')
      .trim();

    const specialCases = {
      'B-CC': 'Bethesda-Chevy Chase',
      'BCC': 'Bethesda-Chevy Chase',
      'Bethesda Chevy Chase': 'Bethesda-Chevy Chase',
      'RMS': 'Richard Montgomery',
      'RM': 'Richard Montgomery',
      'WJ': 'Walter Johnson',
      'Blair': 'Montgomery Blair',
      'Einstein': 'Albert Einstein',
      'Wootton': 'Thomas S. Wootton',
      'Churchill': 'Winston Churchill',
      'Whitman': 'Walt Whitman'
    };

    if (specialCases[normalized]) {
      normalized = specialCases[normalized];
    }

    return normalized;
  };

  const createSchoolKey = (name, type) => {
    const normalized = normalizeSchoolName(name, type);
    return `${type}:${normalized}`;
  };

  // ENHANCED: Extract school name with same logic as SchoolFilter (supports 2024 data structure)
  const extractSchoolName = (feature, index, type) => {
    const props = feature.properties || {};
    
    // 2024-2025 specific field extraction (matching SchoolFilter logic)
    if (currentYear === '2024-2025') {
      if (type === 'elementary') {
        return props.ES_csv_S_N || props.ES_csv_P_N || props.S_NAME || props.NAME || `Elementary School ${index + 1}`;
      } else if (type === 'middleSchool') {
        return props.MS_csv_S_N || props.MS_csv_P_N || props.S_NAME || props.NAME || `Middle School ${index + 1}`;
      } else if (type === 'highSchool') {
        return props.HS_csv_S_N || props.HS_csv_C_N || props.HS_csv_P_N || props.CLUSTER || props.C_NAME || props.S_NAME || props.NAME || `High School ${index + 1}`;
      }
    }
    
    // Standard extraction for other years
    if (type === 'elementary' || type === 'middleSchool') {
      return props.S_NAME || 
             props.SCHNAME || 
             props.NAME ||
             props.SCHOOLNAME ||
             props.School_Name ||
             props.school_name ||
             `${type === 'elementary' ? 'Elementary' : 'Middle'} School ${index + 1}`;
    } else { // highSchool
      return props.CLUSTER || 
             props.C_NAME || 
             props.S_NAME || 
             props.NAME ||
             props.SCHOOLNAME ||
             props.School_Name ||
             props.school_name ||
             props.SCHNAME ||
             `High School ${index + 1}`;
    }
  };

  // FIXED: Check if a school is currently visible on the map (for address lookup display)
  const isSchoolCurrentlyVisible = (schoolName, type) => {
    if (!isFiltering || selectedSchoolNames.size === 0) {
      return true; // Show all schools when not filtering
    }
    
    const schoolKey = createSchoolKey(schoolName, type);
    return selectedSchoolNames.has(schoolKey);
  };

  // Geocode address using Mapbox Geocoding API
  const geocodeAddress = async (address) => {
    if (!address.trim()) return null;
    
    setIsGeocoding(true);
    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(address)}.json?` +
        `access_token=${mapboxgl.accessToken}&` +
        `country=US&` +
        `bbox=-77.5,38.8,-76.8,39.4&` + // Rough bounds for Montgomery County, MD
        `limit=1`
      );
      
      const data = await response.json();
      if (data.features && data.features.length > 0) {
        const [lng, lat] = data.features[0].center;
        return { lng, lat, place_name: data.features[0].place_name };
      }
      return null;
    } catch (error) {
      console.error('Geocoding error:', error);
      return null;
    } finally {
      setIsGeocoding(false);
    }
  };

  // FIXED: Find school assignments - always return the actual school, just note if it's filtered
  const findSchoolAssignments = (lng, lat, data) => {
    console.log('🏫 ADDRESS LOOKUP: Finding school assignments');
    console.log('  Coordinates:', lng, lat);
    console.log('  Is filtering active:', isFiltering);
    console.log('  Selected schools:', Array.from(selectedSchoolNames));
    
    const assignments = {
      elementary: null,
      middleSchool: null,
      highSchool: null
    };

    if (!data) {
      console.log('  ❌ No data available');
      return assignments;
    }

    // Check elementary schools
    if (data.elementary?.features) {
      console.log('  📚 Checking', data.elementary.features.length, 'elementary schools');
      for (let i = 0; i < data.elementary.features.length; i++) {
        const feature = data.elementary.features[i];
        if (feature.geometry?.coordinates?.[0] && isPointInPolygon([lng, lat], feature.geometry.coordinates[0])) {
          const schoolName = extractSchoolName(feature, i, 'elementary');
          const isCurrentlyVisible = isSchoolCurrentlyVisible(schoolName, 'elementary');
          
          console.log(`    Found elementary: "${schoolName}" - Currently visible: ${isCurrentlyVisible}`);
          
          assignments.elementary = {
            name: schoolName,
            properties: feature.properties,
            isCurrentlyVisible: isCurrentlyVisible,
            filterNote: isFiltering && !isCurrentlyVisible ? 'Currently filtered from view' : null
          };
          break;
        }
      }
    }

    // Check middle schools
    if (data.middleSchool?.features) {
      console.log('  🏫 Checking', data.middleSchool.features.length, 'middle schools');
      for (let i = 0; i < data.middleSchool.features.length; i++) {
        const feature = data.middleSchool.features[i];
        if (feature.geometry?.coordinates?.[0] && isPointInPolygon([lng, lat], feature.geometry.coordinates[0])) {
          const schoolName = extractSchoolName(feature, i, 'middleSchool');
          const isCurrentlyVisible = isSchoolCurrentlyVisible(schoolName, 'middleSchool');
          
          console.log(`    Found middle school: "${schoolName}" - Currently visible: ${isCurrentlyVisible}`);
          
          assignments.middleSchool = {
            name: schoolName,
            properties: feature.properties,
            isCurrentlyVisible: isCurrentlyVisible,
            filterNote: isFiltering && !isCurrentlyVisible ? 'Currently filtered from view' : null
          };
          break;
        }
      }
    }

    // Check high schools
    if (data.highSchool?.features) {
      console.log('  🏛️ Checking', data.highSchool.features.length, 'high schools');
      for (let i = 0; i < data.highSchool.features.length; i++) {
        const feature = data.highSchool.features[i];
        if (feature.geometry?.coordinates?.[0] && isPointInPolygon([lng, lat], feature.geometry.coordinates[0])) {
          const schoolName = extractSchoolName(feature, i, 'highSchool');
          const isCurrentlyVisible = isSchoolCurrentlyVisible(schoolName, 'highSchool');
          
          console.log(`    Found high school: "${schoolName}" - Currently visible: ${isCurrentlyVisible}`);
          
          assignments.highSchool = {
            name: schoolName,
            properties: feature.properties,
            isCurrentlyVisible: isCurrentlyVisible,
            filterNote: isFiltering && !isCurrentlyVisible ? 'Currently filtered from view' : null
          };
          break;
        }
      }
    }

    console.log('🏫 ADDRESS LOOKUP: Final assignments', assignments);
    return assignments;
  };

  // Update address assignments when year changes OR when filtering changes
  useEffect(() => {
    if (addressMarker && yearData[currentYear]) {
      console.log('🔄 Updating address assignments due to year/filter change');
      const newAssignments = findSchoolAssignments(
        addressMarker.lng, 
        addressMarker.lat, 
        yearData[currentYear]
      );
      setAddressAssignments(newAssignments);
    }
  }, [currentYear, yearData, addressMarker, selectedSchoolNames, isFiltering]);

  // Handle address search
  const handleAddressSearch = async () => {
    if (!searchAddress.trim()) return;
    
    const result = await geocodeAddress(searchAddress);
    if (result) {
      // Remove existing marker
      if (addressMarker?.mapboxMarker) {
        addressMarker.mapboxMarker.remove();
      }

      // Create new marker - optimized for mobile
      const markerElement = document.createElement('div');
      markerElement.style.cssText = `
        width: ${isMobile ? '32px' : '24px'};
        height: ${isMobile ? '32px' : '24px'};
        background: linear-gradient(135deg, #F59E0B 0%, #D97706 100%);
        border: ${isMobile ? '4px' : '3px'} solid white;
        border-radius: 50%;
        box-shadow: 0 ${isMobile ? '8px 20px' : '4px 12px'} rgba(245, 158, 11, 0.4);
        cursor: pointer;
        transition: transform 0.2s ease;
      `;

      // MOBILE: Add touch feedback
      if (isMobile) {
        markerElement.addEventListener('touchstart', () => {
          markerElement.style.transform = 'scale(1.1)';
        });
        markerElement.addEventListener('touchend', () => {
          markerElement.style.transform = 'scale(1)';
        });
      }

      const mapboxMarker = new mapboxgl.Marker(markerElement)
        .setLngLat([result.lng, result.lat])
        .addTo(map);

      // Store marker info
      const markerInfo = {
        lng: result.lng,
        lat: result.lat,
        address: result.place_name,
        mapboxMarker: mapboxMarker
      };
      
      setAddressMarker(markerInfo);

      // Find current year assignments with filtering awareness
      let assignments = null;
      if (yearData[currentYear]) {
        assignments = findSchoolAssignments(result.lng, result.lat, yearData[currentYear]);
        setAddressAssignments(assignments);
        setShowResults(true);
      }

      // Fly to location - adjust zoom and padding for mobile
      map.flyTo({
        center: [result.lng, result.lat],
        zoom: isMobile ? 13.5 : 14,
        duration: isMobile ? 2000 : 1500,
        padding: isMobile ? { top: 200, bottom: 200, left: 20, right: 20 } : { top: 50, bottom: 50, left: 50, right: 50 }
      });

      // MOBILE: Auto-scroll to results in sidebar mode
      if (isMobile && sidebar) {
        setTimeout(() => {
          const resultsElement = document.querySelector('[data-address-results]');
          if (resultsElement) {
            resultsElement.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'start',
              inline: 'nearest'
            });
          }
        }, 500);
      }

      // Notify parent component
      if (onAddressFound) {
        onAddressFound(markerInfo, assignments);
      }
    }
  };

  // Clear address marker
  const clearAddressMarker = () => {
    if (addressMarker?.mapboxMarker) {
      addressMarker.mapboxMarker.remove();
    }
    setAddressMarker(null);
    setAddressAssignments(null);
    setSearchAddress('');
    setShowResults(false);

    // Notify parent component
    if (onAddressCleared) {
      onAddressCleared();
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (addressMarker?.mapboxMarker) {
        addressMarker.mapboxMarker.remove();
      }
    };
  }, []);

  // MOBILE: Enhanced keyboard handling
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddressSearch();
      // MOBILE: Hide keyboard after search on mobile
      if (isMobile) {
        e.target.blur();
      }
    }
  };

  // MOBILE: Handle input focus for better UX
  const handleInputFocus = (e) => {
    e.target.style.borderColor = 'rgba(245, 158, 11, 0.6)';
    e.target.style.background = 'rgba(255, 255, 255, 0.15)';
    e.target.style.boxShadow = isMobile ? 
      '0 0 0 3px rgba(245, 158, 11, 0.2), 0 4px 12px rgba(0, 0, 0, 0.3)' : 
      '0 0 0 2px rgba(245, 158, 11, 0.2)';
    
    // MOBILE: Scroll input into view on focus
    if (isMobile && sidebar) {
      setTimeout(() => {
        e.target.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
      }, 300);
    }
  };

  const handleInputBlur = (e) => {
    e.target.style.borderColor = 'rgba(255, 255, 255, 0.12)';
    e.target.style.background = 'rgba(255, 255, 255, 0.08)';
    e.target.style.boxShadow = 'none';
  };

  // FIXED: Render school assignment - always show school name, just indicate if filtered
  const renderSchoolAssignment = (assignment, type, color, label) => {
    if (!assignment) {
      return (
        <div style={{
          padding: isMobile ? '12px 16px' : '8px 12px',
          background: `rgba(${type === 'elementary' ? '59, 130, 246' : type === 'middleSchool' ? '16, 185, 129' : '239, 68, 68'}, 0.1)`,
          border: `1px solid rgba(${type === 'elementary' ? '59, 130, 246' : type === 'middleSchool' ? '16, 185, 129' : '239, 68, 68'}, 0.3)`,
          borderRadius: isMobile ? '12px' : '6px',
          fontSize: isMobile ? '14px' : '12px'
        }}>
          <div style={{ color: color, fontWeight: '600', marginBottom: '4px' }}>
            {label}:
          </div>
          <div style={{ color: 'rgba(255, 255, 255, 0.7)', fontStyle: 'italic' }}>
            Not found
          </div>
        </div>
      );
    }

    return (
      <div style={{
        padding: isMobile ? '12px 16px' : '8px 12px',
        background: `rgba(${type === 'elementary' ? '59, 130, 246' : type === 'middleSchool' ? '16, 185, 129' : '239, 68, 68'}, 0.1)`,
        border: `1px solid rgba(${type === 'elementary' ? '59, 130, 246' : type === 'middleSchool' ? '16, 185, 129' : '239, 68, 68'}, 0.3)`,
        borderRadius: isMobile ? '12px' : '6px',
        fontSize: isMobile ? '14px' : '12px',
        // MOBILE: Better touch feedback
        transition: 'all 0.2s ease'
      }}>
        <div style={{ 
          color: color, 
          fontWeight: '600', 
          marginBottom: '4px', 
          display: 'flex', 
          alignItems: 'center', 
          gap: '8px',
          flexWrap: 'wrap'
        }}>
          <span>{label}:</span>
          {!assignment.isCurrentlyVisible && (
            <span style={{
              fontSize: isMobile ? '11px' : '9px',
              background: 'rgba(239, 68, 68, 0.2)',
              color: '#F87171',
              padding: isMobile ? '2px 6px' : '1px 4px',
              borderRadius: '4px',
              fontWeight: '500',
              border: '1px solid rgba(239, 68, 68, 0.3)'
            }}>
              FILTERED
            </span>
          )}
        </div>
        <div style={{ 
          color: 'rgba(255, 255, 255, 0.95)', 
          fontWeight: '600',
          fontSize: isMobile ? '15px' : '13px',
          lineHeight: '1.3',
          marginBottom: assignment.filterNote ? '6px' : '0'
        }}>
          {assignment.name}
        </div>
        {assignment.filterNote && (
          <div style={{
            fontSize: isMobile ? '12px' : '10px',
            color: 'rgba(239, 68, 68, 0.8)',
            fontStyle: 'italic',
            lineHeight: '1.2'
          }}>
            {assignment.filterNote}
          </div>
        )}
      </div>
    );
  };

  // MOBILE: Dynamic container styles for mobile optimization
  const getContainerStyle = () => {
    if (sidebar) {
      return {
        background: 'transparent',
        backdropFilter: 'none',
        borderRadius: '0',
        padding: '0',
        border: 'none',
        boxShadow: 'none',
        width: '100%',
        minWidth: 'auto',
        maxWidth: 'none',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Inter", sans-serif'
      };
    }

    return {
      background: isMobile ? 
        'rgba(15, 20, 25, 0.98)' : 
        'rgba(15, 20, 25, 0.9)',
      backdropFilter: isMobile ? 'blur(32px)' : 'blur(20px)',
      borderRadius: isMobile ? '24px' : '16px',
      padding: isMobile ? '28px 24px' : '20px',
      border: `1px solid rgba(255, 255, 255, ${isMobile ? '0.15' : '0.1'})`,
      boxShadow: isMobile ? 
        '0 16px 48px rgba(0, 0, 0, 0.7), 0 8px 24px rgba(0, 0, 0, 0.4)' :
        '0 8px 20px rgba(0, 0, 0, 0.4)',
      minWidth: isMobile ? '320px' : '320px',
      maxWidth: isMobile ? '90vw' : '400px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Inter", sans-serif',
      // MOBILE: Enhanced touch handling
      touchAction: 'manipulation',
      userSelect: 'none',
      WebkitTapHighlightColor: 'transparent',
      // MOBILE: Ensure proper z-index for mobile
      position: 'relative',
      zIndex: isMobile ? 10 : 'auto'
    };
  };

  return (
    <div style={getContainerStyle()}>
      {/* Header - only show in standalone mode */}
      {!sidebar && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: isMobile ? '16px' : '12px',
          marginBottom: isMobile ? '24px' : '16px'
        }}>
          <div style={{
            width: isMobile ? '40px' : '32px',
            height: isMobile ? '40px' : '32px',
            background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
            borderRadius: isMobile ? '12px' : '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: isMobile ? 
              '0 12px 32px rgba(245, 158, 11, 0.4), 0 4px 16px rgba(0, 0, 0, 0.3)' :
              '0 8px 20px rgba(245, 158, 11, 0.3)',
            border: '1px solid rgba(255, 255, 255, 0.15)'
          }}>
            <svg width={isMobile ? '20' : '16'} height={isMobile ? '20' : '16'} viewBox="0 0 24 24" fill="white">
              <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
            </svg>
          </div>
          <div>
            <h3 style={{ 
              margin: '0', 
              fontSize: isMobile ? '20px' : '16px', 
              fontWeight: '700',
              color: 'rgba(255, 255, 255, 0.95)',
              letterSpacing: '-0.02em'
            }}>
              Address Lookup
            </h3>
            <p style={{
              margin: '4px 0 0 0',
              fontSize: isMobile ? '14px' : '12px',
              color: 'rgba(255, 255, 255, 0.6)',
              fontWeight: '500'
            }}>
              Find school assignments
            </p>
          </div>
        </div>
      )}

      {/* ENHANCED: Filter status indicator for mobile */}
      {isFiltering && (
        <div style={{
          background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.12) 0%, rgba(16, 185, 129, 0.06) 100%)',
          border: '1px solid rgba(16, 185, 129, 0.3)',
          borderRadius: isMobile ? '12px' : '6px',
          padding: isMobile ? '12px 16px' : '8px 10px',
          marginBottom: isMobile ? '20px' : '12px',
          fontSize: isMobile ? '13px' : '11px'
        }}>
          <div style={{
            color: 'rgba(16, 185, 129, 0.9)',
            fontWeight: '600',
            marginBottom: '4px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}>
            <svg width={isMobile ? '14' : '12'} height={isMobile ? '14' : '12'} viewBox="0 0 24 24" fill="currentColor">
              <path d="M9.5,3A6.5,6.5 0 0,1 16,9.5C16,11.11 15.41,12.59 14.44,13.73L14.71,14H15.5L20.5,19L19,20.5L14,15.5V14.71L13.73,14.44C12.59,15.41 11.11,16 9.5,16A6.5,6.5 0 0,1 3,9.5A6.5,6.5 0 0,1 9.5,3M9.5,5C7,5 5,7 5,9.5C5,12 7,14 9.5,14C12,14 14,12 14,9.5C14,7 12,5 9.5,5Z"/>
            </svg>
            <span>Filter Active</span>
          </div>
          <div style={{
            color: 'rgba(255, 255, 255, 0.7)',
            lineHeight: '1.4'
          }}>
            Showing {selectedSchoolNames.size} selected school{selectedSchoolNames.size !== 1 ? 's' : ''}. Assignment results show actual schools regardless of filter.
          </div>
        </div>
      )}

      {/* MOBILE OPTIMIZED: Search Input */}
      <div style={{
        display: 'flex',
        gap: isMobile ? '12px' : '8px',
        marginBottom: isMobile ? '24px' : '16px',
        flexDirection: isMobile ? 'column' : 'row'
      }}>
        <input
          type="text"
          value={searchAddress}
          onChange={(e) => setSearchAddress(e.target.value)}
          onKeyPress={handleKeyPress}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          placeholder={isMobile ? "Enter address (e.g., 123 Main St, Rockville)" : "Enter address (e.g., 123 Main St, Rockville, MD)"}
          disabled={isGeocoding}
          style={{
            flex: 1,
            padding: isMobile ? '16px 20px' : '10px 12px',
            background: 'rgba(255, 255, 255, 0.08)',
            border: '1px solid rgba(255, 255, 255, 0.12)',
            borderRadius: isMobile ? '16px' : '8px',
            color: 'rgba(255, 255, 255, 0.95)',
            fontSize: isMobile ? '16px' : '13px', // 16px prevents zoom on iOS
            fontFamily: 'inherit',
            outline: 'none',
            transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
            // MOBILE: Enhanced touch handling
            touchAction: 'manipulation',
            WebkitAppearance: 'none',
            appearance: 'none',
            // MOBILE: Better mobile styling
            boxSizing: 'border-box',
            width: '100%'
          }}
        />
        <button
          onClick={handleAddressSearch}
          disabled={isGeocoding || !searchAddress.trim()}
          style={{
            padding: isMobile ? '16px 24px' : '10px 16px',
            background: isGeocoding ? 'rgba(107, 114, 128, 0.5)' : 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
            border: 'none',
            borderRadius: isMobile ? '16px' : '8px',
            color: 'white',
            fontSize: isMobile ? '16px' : '13px',
            fontWeight: '600',
            cursor: isGeocoding || !searchAddress.trim() ? 'not-allowed' : 'pointer',
            transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
            opacity: isGeocoding || !searchAddress.trim() ? 0.5 : 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: isMobile ? '10px' : '6px',
            flexShrink: 0,
            width: isMobile ? '100%' : 'auto',
            // MOBILE: Enhanced touch handling
            touchAction: 'manipulation',
            WebkitTapHighlightColor: 'transparent',
            // MOBILE: Minimum touch target size
            minHeight: isMobile ? '52px' : 'auto',
            boxShadow: !isGeocoding && searchAddress.trim() ? 
              '0 4px 12px rgba(245, 158, 11, 0.3)' : 
              'none'
          }}
          onTouchStart={(e) => {
            if (!isGeocoding && searchAddress.trim()) {
              e.target.style.transform = 'scale(0.96)';
              e.target.style.boxShadow = '0 2px 8px rgba(245, 158, 11, 0.4)';
            }
          }}
          onTouchEnd={(e) => {
            if (!isGeocoding && searchAddress.trim()) {
              e.target.style.transform = 'scale(1)';
              e.target.style.boxShadow = '0 4px 12px rgba(245, 158, 11, 0.3)';
            }
          }}
        >
          {isGeocoding ? (
            <>
              <div style={{
                width: isMobile ? '18px' : '12px',
                height: isMobile ? '18px' : '12px',
                border: `${isMobile ? '2.5px' : '1.5px'} solid rgba(255, 255, 255, 0.3)`,
                borderTop: `${isMobile ? '2.5px' : '1.5px'} solid white`,
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}></div>
              {isMobile ? 'Searching...' : 'Finding...'}
            </>
          ) : (
            <>
              <svg width={isMobile ? '18' : '14'} height={isMobile ? '18' : '14'} viewBox="0 0 24 24" fill="currentColor">
                <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
              </svg>
              Search
            </>
          )}
        </button>
      </div>

      {/* MOBILE OPTIMIZED: Clear button */}
      {addressMarker && (
        <button
          onClick={clearAddressMarker}
          style={{
            width: '100%',
            padding: isMobile ? '14px 16px' : '8px',
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: isMobile ? '12px' : '6px',
            color: 'rgba(239, 68, 68, 0.9)',
            fontSize: isMobile ? '15px' : '12px',
            fontWeight: '600',
            cursor: 'pointer',
            marginBottom: isMobile ? '24px' : '16px',
            transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
            // MOBILE: Enhanced touch handling
            touchAction: 'manipulation',
            WebkitTapHighlightColor: 'transparent',
            minHeight: isMobile ? '48px' : 'auto'
          }}
          onMouseEnter={(e) => {
            if (!isMobile) {
              e.target.style.background = 'rgba(239, 68, 68, 0.15)';
            }
          }}
          onMouseLeave={(e) => {
            if (!isMobile) {
              e.target.style.background = 'rgba(239, 68, 68, 0.1)';
            }
          }}
          onTouchStart={(e) => {
            e.target.style.transform = 'scale(0.98)';
            e.target.style.background = 'rgba(239, 68, 68, 0.15)';
          }}
          onTouchEnd={(e) => {
            e.target.style.transform = 'scale(1)';
            e.target.style.background = 'rgba(239, 68, 68, 0.1)';
          }}
        >
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
          }}>
            <svg width={isMobile ? '16' : '14'} height={isMobile ? '16' : '14'} viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
            Clear Address
          </div>
        </button>
      )}

      {/* MOBILE OPTIMIZED: Address Info */}
      {addressMarker && (
        <div style={{
          background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.12) 0%, rgba(245, 158, 11, 0.06) 100%)',
          border: '1px solid rgba(245, 158, 11, 0.3)',
          borderRadius: isMobile ? '16px' : '8px',
          padding: isMobile ? '18px 20px' : '12px',
          marginBottom: isMobile ? '24px' : '16px',
          backdropFilter: 'blur(8px)'
        }}>
          <div style={{
            fontSize: isMobile ? '15px' : '12px',
            color: 'rgba(245, 158, 11, 0.9)',
            fontWeight: '600',
            marginBottom: '6px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <svg width={isMobile ? '16' : '14'} height={isMobile ? '16' : '14'} viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
            </svg>
            Selected Address
          </div>
          <div style={{
            fontSize: isMobile ? '14px' : '11px',
            color: 'rgba(255, 255, 255, 0.8)',
            lineHeight: '1.4',
            fontWeight: '500'
          }}>
            {addressMarker.address}
          </div>
        </div>
      )}

      {/* MOBILE OPTIMIZED: School Assignments */}
      {addressAssignments && showResults && (
        <div data-address-results>
          <div style={{
            fontSize: isMobile ? '18px' : '14px',
            fontWeight: '700',
            color: 'rgba(255, 255, 255, 0.95)',
            marginBottom: isMobile ? '20px' : '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <svg width={isMobile ? '20' : '16'} height={isMobile ? '20' : '16'} viewBox="0 0 24 24" fill="#3B82F6">
              <path d="M12 3L1 9l4 2.18v6L12 21l7-3.82v-6l2-1.09V17h2V9L12 3zm6.82 6L12 12.72 5.18 9 12 5.28 18.82 9zM17 15.99l-5 2.73-5-2.73v-3.72L12 15l5-2.73v3.72z"/>
            </svg>
            <span>School Assignments ({currentYear})</span>
          </div>
          
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: isMobile ? '16px' : '8px' 
          }}>
            {/* Elementary */}
            {renderSchoolAssignment(
              addressAssignments.elementary,
              'elementary',
              '#60A5FA',
              'Elementary School'
            )}

            {/* Middle School */}
            {renderSchoolAssignment(
              addressAssignments.middleSchool,
              'middleSchool',
              '#34D399',
              'Middle School'
            )}

            {/* High School */}
            {renderSchoolAssignment(
              addressAssignments.highSchool,
              'highSchool',
              '#F87171',
              'High School'
            )}
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        /* MOBILE: Prevent zoom on input focus in iOS */
        @media screen and (max-width: 768px) {
          input[type="text"] {
            font-size: 16px !important;
            transform: scale(1);
            transition: none;
          }
        }

        /* MOBILE: Better touch targets */
        @media (hover: none) and (pointer: coarse) {
          button {
            min-height: 44px;
          }
          
          input {
            min-height: 44px;
          }
        }

        /* Disable text selection on mobile for better UX */
        @media (max-width: 768px) {
          * {
            -webkit-touch-callout: none;
            -webkit-user-select: none;
            user-select: none;
          }
          
          input, textarea {
            -webkit-user-select: text;
            user-select: text;
          }
        }

        /* MOBILE: Enhanced scrollbar for webkit browsers */
        @media (max-width: 768px) {
          ::-webkit-scrollbar {
            width: 4px;
          }
          ::-webkit-scrollbar-track {
            background: rgba(255, 255, 255, 0.05);
            border-radius: 2px;
          }
          ::-webkit-scrollbar-thumb {
            background: rgba(255, 255, 255, 0.2);
            border-radius: 2px;
          }
          ::-webkit-scrollbar-thumb:hover {
            background: rgba(255, 255, 255, 0.3);
          }
        }
      `}</style>
    </div>
  );
};

export default AddressLookup;